import React from 'react';
import ReactPlayer from 'react-player/vimeo';

import { useAddVideoCompleteMutation, useAddVideoPlayMutation } from '@edx/graphql/generated';
import { useAuth } from '@edx/hooks/use-auth';

type Props = {
  /* EDX Id */
  videoId?: string;
  /* Vimeo Id */
  embedId: number;
  playing?: boolean;
  autoPlay?: boolean;
};

// https://github.com/vimeo/player.js#embed-options
const DEFAULT_PLAYER_OPTIONS = {
  responsive: true,
  quality: '720p',
  color: 'ED0B24',
  playsinline: false,
  speed: true,
  title: false,
  byline: false,
};

export const Player: React.FC<Props> = ({ videoId, embedId, playing = false, autoPlay = false }) => {
  const { isLoggedIn } = useAuth();
  const [didPlay, setDidPlay] = React.useState(false);
  const [onAddVideoComplete] = useAddVideoCompleteMutation();
  const [onAddVideoPlay] = useAddVideoPlayMutation();

  React.useEffect(() => {
    setDidPlay(false);
  }, [videoId]);

  const handleVideoPlay = async (): Promise<void> => {
    if (didPlay || !isLoggedIn || !videoId) return;

    const { data } = await onAddVideoPlay({ variables: { input: { videoId } } });

    if (data?.addVideoPlay.id === videoId) setDidPlay(true);
  };

  const handleVideoEnded = (): void => {
    if (!isLoggedIn || !videoId) return;

    onAddVideoComplete({ variables: { input: { videoId } } });
  };

  return (
    <div className="relative aspect-ratio-16/9" data-testid="player">
      <div className="absolute inset-0">
        <ReactPlayer
          url={`https://vimeo.com/${embedId}`}
          controls
          playing={playing}
          width="100%"
          height="100%"
          config={{ vimeo: { playerOptions: { ...DEFAULT_PLAYER_OPTIONS, id: embedId, autoPlay } } }}
          onPlay={handleVideoPlay}
          onEnded={handleVideoEnded}
        />
      </div>
    </div>
  );
};
